import { Product, Category } from '../../data/products';

export interface AdminUser {
  email: string;
  name: string;
  role: 'admin' | 'editor';
}

export interface OrderItem {
  product: Product;
  quantity: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  shippingAddress: string;
  billingAddress?: string;
  city: string;
  district: string;
  postalCode: string;
  items: OrderItem[];
  totalAmount: number;
  subtotal?: number;
  shippingCost: number;
  couponCode?: string;
  couponDiscount?: number;
  status: OrderStatus;
  paymentMethod: string;
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  trackingNumber?: string;
  cargoCompany?: string;
  notes: string;
  userId?: string | null;
  returnRequest?: ReturnRequest | null;
  createdAt: string;
  updatedAt: string;
}

export type OrderStatus = 
  | 'pending' 
  | 'processing' 
  | 'shipped' 
  | 'in_transit' 
  | 'delivered' 
  | 'cancelled'
  | 'returned'
  | 'return_requested';

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Beklemede',
  processing: 'Hazırlanıyor',
  shipped: 'Kargoya Verildi',
  in_transit: 'Yolda',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal Edildi',
  returned: 'İade Edildi',
  return_requested: 'İade Talep Edildi',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  in_transit: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  returned: 'bg-orange-100 text-orange-800',
  return_requested: 'bg-amber-100 text-amber-800',
};

export interface StoreSettings {
  storeName: string;
  storeEmail: string;
  storePhone: string;
  storeAddress: string;
  currency: string;
  shippingCost: number;
  freeShippingMin: number;
  taxRate: number;
  maintenanceMode: boolean;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  district: string;
  postalCode: string;
  notes: string;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Notification {
  id: string;
  type: 'success' | 'warning' | 'info' | 'error';
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  icon?: 'order' | 'product' | 'delete' | 'status' | 'system';
}

export interface ReturnRequest {
  reason: string;
  description: string;
  requestedAt: string;
  status: 'pending' | 'approved' | 'rejected';
}

export interface Coupon {
  id: string;
  code: string;
  description: string;
  type: 'percentage' | 'fixed';
  value: number;
  minOrderAmount: number;
  maxDiscountAmount: number | null;
  usageLimit: number | null;
  usageCount: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CouponType = 'percentage' | 'fixed';

export const COUPON_TYPE_LABELS: Record<CouponType, string> = {
  percentage: 'Yüzde (%)',
  fixed: 'Sabit Tutar (₺)',
};

export interface AdminState {
  isAuthenticated: boolean;
  user: AdminUser | null;
  token: string | null;
  products: Product[];
  orders: Order[];
  categories: Category[];
  customers: Customer[];
  coupons: Coupon[];
  settings: StoreSettings;
  sidebarOpen: boolean;
  notifications: Notification[];

  // Auth
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  fetchProductsFromAPI: () => Promise<void>;
  fetchCategoriesFromAPI: () => Promise<void>;
  fetchCouponsFromAPI: () => Promise<void>;
  fetchSettingsFromAPI: () => Promise<void>;

  // Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Notifications
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;

  // Products
  addProduct: (product: Omit<Product, 'id'>) => Product;
  updateProduct: (id: number, updates: Partial<Product>) => void;
  deleteProduct: (id: number) => void;
  toggleProductFeatured: (id: number) => void;
  toggleProductCampaign: (id: number) => void;
  toggleProductNew: (id: number) => void;
  updateProductStock: (id: number, stock: number) => void;

  // Orders
  addOrder: (order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>) => Order;
  updateOrderStatus: (id: string, status: OrderStatus) => void;
  updateOrderPaymentStatus: (id: string, status: Order['paymentStatus']) => void;
  updateOrderNotes: (id: string, notes: string) => void;
  deleteOrder: (id: string) => void;
  requestReturn: (orderId: string, reason: string, description: string) => void;
  fetchOrdersFromAPI: () => Promise<void>;

  // Customers
  addCustomer: (customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) => Customer;
  updateCustomer: (id: string, updates: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;
  getCustomerById: (id: string) => Customer | undefined;
  getCustomerByEmail: (email: string) => Customer | undefined;
  getCustomerOrders: (customerId: string) => Order[];

  // Categories
  addCategory: (category: Category) => void;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  deleteCategory: (id: string) => void;

  // Coupons
  addCoupon: (coupon: Omit<Coupon, 'id' | 'usageCount' | 'createdAt' | 'updatedAt'>) => Coupon;
  updateCoupon: (id: string, updates: Partial<Coupon>) => void;
  deleteCoupon: (id: string) => void;
  toggleCouponActive: (id: string) => void;
  validateCoupon: (code: string, orderAmount: number) => Promise<{ valid: boolean; discount: number; coupon?: Coupon; error?: string }>;

  // Settings
  updateSettings: (settings: Partial<StoreSettings>) => void;

  // Helpers
  getProductById: (id: number) => Product | undefined;
  getOrderById: (id: string) => Order | undefined;
  getOrdersByStatus: (status: OrderStatus) => Order[];
  getTotalRevenue: () => number;
  getTotalOrders: () => number;
  getTotalProducts: () => number;
  getRecentOrders: (count: number) => Order[];
  getTotalCustomers: () => number;
}
